import { styled, SxProps } from '@mui/material/styles';
import React, { useEffect, useRef, useState } from 'react';

import ScrollButton from './ScrollButton';
import { NavigationDirection, ScrollOrientation } from './types';

type ScrollableListProps = {
    count: number;
    children: React.ReactNode;
    scrollStepPx: number;
    gapPx: number;
    orientation?: ScrollOrientation;
    idPrefix?: string;
    sx?: SxProps;
};

const ScrollableListContainer = styled('div', {
    name: 'ScrollableListContainer',
    shouldForwardProp: (prop) => prop !== 'gapPx' && prop !== 'orientation',
})<{ gapPx: number; orientation: ScrollOrientation }>(
    ({ gapPx, orientation }) => ({
        display: 'flex',
        gap: gapPx,
        flexDirection: orientation === 'vertical' ? 'column' : 'row',
        overflowX: orientation === 'horizontal' ? 'auto' : 'hidden',
        overflowY: orientation === 'vertical' ? 'auto' : 'hidden',
        width: '100%',
        height: orientation === 'vertical' ? '100%' : undefined,
    }),
);

const ScrollableList = ({
    count,
    children,
    scrollStepPx,
    gapPx,
    orientation = 'horizontal',
    idPrefix = undefined,
    sx = undefined,
}: ScrollableListProps): React.ReactElement => {
    const navRef = useRef<HTMLDivElement>(null);
    const [canScrollToStart, setCanScrollToStart] = useState(false);
    const [canScrollToEnd, setCanScrollToEnd] = useState(false);

    useEffect(() => {
        const container = navRef.current;
        if (container) {
            const handleScroll = () => {
                if (orientation === 'horizontal') {
                    setCanScrollToStart(container.scrollLeft > 0);
                    setCanScrollToEnd(
                        container.scrollWidth >
                            container.clientWidth + container.scrollLeft,
                    );
                } else {
                    setCanScrollToStart(container.scrollTop > 0);
                    setCanScrollToEnd(
                        container.scrollHeight >
                            container.clientHeight + container.scrollTop,
                    );
                }
            };
            handleScroll();
            container.addEventListener('scroll', handleScroll);
            return () => {
                container.removeEventListener('scroll', handleScroll);
            };
        }
        return undefined;
    }, [count, orientation]);

    const handleNavigation = (direction: NavigationDirection) => {
        if (!navRef.current) return;
        const scrollOffset = scrollStepPx + gapPx;
        if (direction === 'left' || direction === 'up') {
            if (orientation === 'horizontal') {
                navRef.current.scrollLeft -= scrollOffset;
            } else {
                navRef.current.scrollTop -= scrollOffset;
            }
        } else {
            if (orientation === 'horizontal') {
                navRef.current.scrollLeft += scrollOffset;
            } else {
                navRef.current.scrollTop += scrollOffset;
            }
        }
    };

    const startDirection: NavigationDirection =
        orientation === 'horizontal' ? 'left' : 'up';
    const endDirection: NavigationDirection =
        orientation === 'horizontal' ? 'right' : 'down';

    return (
        <div
            style={{
                position: 'relative',
                ...(orientation === 'vertical' && {
                    minHeight: 0,
                }),
            }}
        >
            {canScrollToStart && (
                <ScrollButton
                    direction={startDirection}
                    handleNavigation={handleNavigation}
                    idPrefix={idPrefix}
                />
            )}
            <ScrollableListContainer
                ref={navRef}
                gapPx={gapPx}
                orientation={orientation}
                sx={sx}
            >
                {children}
            </ScrollableListContainer>
            {canScrollToEnd && (
                <ScrollButton
                    direction={endDirection}
                    handleNavigation={handleNavigation}
                    idPrefix={idPrefix}
                    onClick={() => setCanScrollToStart(true)}
                />
            )}
        </div>
    );
};

export default ScrollableList;
